import { type RefObject } from 'react';
import {
  MyNazcaImageEditorPreview,
  type EditSection,
  type MyNazcaImageEditorPreviewProps,
} from './MyNazcaImageEditorPreview';
import { type MyNazcaImageEditorControlsProps } from './MyNazcaImageEditorControls';
import { MyNazcaImageEditorFields } from './MyNazcaImageEditorFields';
import { MyNazcaMobileStepLayout } from './MyNazcaMobileStepLayout';

type MyNazcaMobileStep1EditorProps = {
  lang: 'ja' | 'en';
  busy: string | null;
  imageFileName: string | null;
  openEditSection: EditSection | null;
  canUndo: boolean;
  canRedo: boolean;
  canGoNext: boolean;
  stepBarRef: RefObject<HTMLElement | null>;
  onSelectImage: (file: File) => void;
  onSelectEditSection: (section: EditSection) => void;
  onUndo: () => void;
  onRedo: () => void;
  onNext: () => void;
  previewProps: MyNazcaImageEditorPreviewProps;
  controlsProps: Omit<MyNazcaImageEditorControlsProps, 'section' | 'variant' | 'lang' | 'busy'>;
};

export function MyNazcaMobileStep1Editor({
  lang,
  busy,
  imageFileName,
  openEditSection,
  canUndo,
  canRedo,
  canGoNext,
  stepBarRef,
  onSelectImage,
  onSelectEditSection,
  onUndo,
  onRedo,
  onNext,
  previewProps,
  controlsProps,
}: MyNazcaMobileStep1EditorProps) {
  const activeSection = openEditSection ?? 'crop';

  return (
    <MyNazcaMobileStepLayout
      lang={lang}
      stepBarRef={stepBarRef}
      preview={(
        <MyNazcaImageEditorPreview
          {...previewProps}
          className="mynazca-app__section--image-editor--mobile-dock"
        />
      )}
    >
      <div className="mynazca-app__section">
        <h2 className="mynazca-app__section-title mynazca-app__section-title--sub">
          {lang === 'ja' ? '画像を編集する' : 'Edit image'}
        </h2>

        <div className="mynazca-app__file-picker mynazca-app__file-picker--compact">
          <input
            id="mynazca-image-mobile"
            type="file"
            accept="image/*"
            className="mynazca-app__file-input"
            disabled={busy != null}
            onChange={(e) => {
              const file = e.currentTarget.files?.[0];
              if (file) onSelectImage(file);
            }}
          />
          <label
            htmlFor="mynazca-image-mobile"
            className={`mynazca-app__file-btn mynazca-app__file-btn--primary${imageFileName ? ' mynazca-app__file-btn--has-file' : ''}`}
            data-disabled={busy != null ? '' : undefined}
            title={imageFileName ?? undefined}
          >
            <span className="mynazca-app__file-btn-label">
              {imageFileName ?? (lang === 'ja' ? '別の画像' : 'Change image')}
            </span>
          </label>
        </div>

        <MyNazcaImageEditorFields
          lang={lang}
          busy={busy}
          activeSection={activeSection}
          canUndo={canUndo}
          canRedo={canRedo}
          onSelectSection={onSelectEditSection}
          onUndo={onUndo}
          onRedo={onRedo}
          controlsProps={controlsProps}
        />

        <div className="mynazca-app__nav mynazca-app__nav--end mynazca-app__nav--mobile-sticky">
          <button
            type="button"
            className="mynazca-app__btn-primary"
            onClick={() => onNext()}
            disabled={!canGoNext || busy != null}
          >
            {lang === 'ja' ? '次へ →' : 'Next →'}
          </button>
        </div>
      </div>
    </MyNazcaMobileStepLayout>
  );
}
